import React, { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ButtonBase, List, Menu, Toast, useThemeMode } from "@sscs/ui";
import { ArrowDropDownIcon, DarkModeIcon, LightModeIcon } from "@sscs/ui/icons";
import { UserAvatar } from "Components";
import {
  APPEARANCE_ROUTE,
  CHANGE_PASSWORD_ROUTE,
  PERSONAL_ACCESS_TOKEN_ROUTE,
  PROFILE_ROUTE,
  USER_SETTINGS_ROUTE,
} from "Constants";
import { useAppDispatch, useAppSelector, useLoginInfo } from "Hooks";
import {
  getToastNotificationId,
  selectAllUnsavedChanges,
  selectHasUnsavedChanges,
} from "Modules/notification";
import { baseApi, useGetWhoamiQuery } from "Services";
import { useLogoutMutation } from "../../../services/auth.api";
import styles from "./userInfo.module.scss";

type SettingsMenuItem = {
  id: string;
  to: string;
  label: string;
};

const UserInfo = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { data: whoami } = useGetWhoamiQuery();
  const { isSSOLogin, hasLoaded } = useLoginInfo();
  const { mode, toggleAppearance } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const unsavedChanges = useAppSelector(selectAllUnsavedChanges);
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);

  const handleLogout = async () => {
    await logout();
    // Clear RTK Query cache to void any queries in flight after logging out
    dispatch(baseApi.util.resetApiState());
    navigate("/login", { replace: true });
  };

  const handleMaybeLogout = async () => {
    if (hasUnsavedChanges) {
      Object.entries(unsavedChanges).forEach(
        ([page, unsavedChangesPerPage]) => {
          Object.entries(unsavedChangesPerPage).forEach(
            ([id, unsavedChange]) => {
              const toastId = getToastNotificationId(page, id);

              Toast.show({
                title: `${unsavedChange.text}. Are you sure you want to logout without saving?`,
                actions: [
                  {
                    label: "Show me",
                    onClick: () => navigate(unsavedChange.navigateTo),
                  },
                  {
                    label: "Logout",
                    variant: "secondary",
                    onClick: handleLogout,
                  },
                ],
                type: "warning",
                props: {
                  toastId: toastId,
                  autoClose: false,
                  closeButton: false,
                  closeOnClick: false,
                },
              });
            },
          );
        },
      );
    } else {
      await handleLogout();
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const hideChangePassword = isSSOLogin && hasLoaded;
  const isDark = mode === "dark";

  const settingsItems: SettingsMenuItem[] = [
    {
      id: "user-details",
      to: `/${USER_SETTINGS_ROUTE}/${PROFILE_ROUTE}`,
      label: "Profile",
    },
    ...(hideChangePassword
      ? []
      : [
          {
            id: "change-password",
            to: `/${USER_SETTINGS_ROUTE}/${CHANGE_PASSWORD_ROUTE}`,
            label: "Change password",
          },
        ]),
    {
      id: "personal-access-tokens",
      to: `/${USER_SETTINGS_ROUTE}/${PERSONAL_ACCESS_TOKEN_ROUTE}`,
      label: "Personal access tokens",
    },
    {
      id: "appearance",
      to: `/${USER_SETTINGS_ROUTE}/${APPEARANCE_ROUTE}`,
      label: "Appearance",
    },
  ];

  return (
    <div id="user-info" className={styles["login-info"]}>
      <ButtonBase
        className={styles["user-button"]}
        aria-label="User menu"
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          handleOpenMenuClick(event);
        }}
      >
        <UserAvatar user={whoami} showTooltip={false} />
        <ArrowDropDownIcon className={styles["user-icon"]} />
      </ButtonBase>

      <Menu.Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        keepMounted
        anchorOrigin={{
          vertical: 30,
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Menu.GroupTitle key="settings">Settings</Menu.GroupTitle>
        {settingsItems.map((item) => (
          <Menu.Item
            key={item.id}
            id={item.id}
            disabled={whoami?.email === undefined}
            component={Link}
            to={item.to}
          >
            {item.label}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item id="toggle-theme" onClick={toggleAppearance}>
          <List.ItemIcon>
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </List.ItemIcon>
          <List.ItemText>
            {isDark ? "Switch to light theme" : "Switch to dark theme"}
          </List.ItemText>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          id="logout"
          disabled={whoami?.email === undefined}
          onClick={handleMaybeLogout}
        >
          Logout
        </Menu.Item>
      </Menu.Menu>
    </div>
  );
};

export default UserInfo;
